import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, useAuth } from "@/context/AuthContext";
import { openBookingPayment } from "@/lib/razorpay";
import { Kicker, MaskedLine, Reveal } from "@/components/Motion";
import StatusBadge from "@/components/StatusBadge";
import { serviceBySlug } from "@/data/services";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

export default function MyBookings() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState(null);

  useEffect(() => {
    if (!user) return;
    api
      .get("/bookings/my")
      .then((r) => setBookings(r.data))
      .catch(() => setBookings([]));
  }, [user]);

  const payNow = async (b) => {
    try {
      const { data: cfg } = await api.get("/payments/config");
      if (!cfg.configured) {
        toast.error("Online payment is not enabled yet — please pay at the service desk.");
        return;
      }
      const refresh = () => api.get("/bookings/my").then((r) => setBookings(r.data));
      await openBookingPayment({
        bookingId: b.id,
        details: { name: b.name, email: b.email, phone: b.phone },
        onPaid: refresh,
        onFailed: refresh,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canPay = (b) =>
    b.fee_paise > 0 &&
    ["unpaid", "pending_payment", "failed"].includes(b.payment_status) &&
    b.payment_method !== "offline";

  if (user === null) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center pt-16">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Loading…</p>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <div data-testid="my-bookings-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <Kicker>My Account</Kicker>
          <h1 className="mt-3 font-display font-bold tracking-tight text-slate-900">
            <MaskedLine delay={0.1} className="text-3xl sm:text-4xl">
              My Bookings & Payments
            </MaskedLine>
          </h1>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {bookings === null && (
          <div className="space-y-4" data-testid="bookings-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-xl border border-slate-200 bg-white" />
            ))}
          </div>
        )}

        {bookings !== null && bookings.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid="bookings-empty">
            <p className="text-slate-600">You have no bookings yet.</p>
            <Link
              to="/book"
              data-testid="bookings-empty-cta"
              className="mt-6 inline-block rounded-md bg-blue-900 px-6 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
            >
              Book your first service
            </Link>
          </div>
        )}

        {bookings !== null && bookings.length > 0 && (
          <>
            <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm md:block">
              <table className="w-full text-left text-sm" data-testid="bookings-table">
                <thead>
                  <tr className="border-b border-slate-200 font-mono text-[11px] uppercase tracking-[0.12em] text-slate-500">
                    <th className="px-5 py-3.5 font-medium">Booking ID</th>
                    <th className="px-5 py-3.5 font-medium">Service</th>
                    <th className="px-5 py-3.5 font-medium">Booking Date</th>
                    <th className="px-5 py-3.5 font-medium">Status</th>
                    <th className="px-5 py-3.5 font-medium">Payment</th>
                    <th className="px-5 py-3.5 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-slate-100 last:border-0" data-testid={`booking-row-${b.id}`}>
                      <td className="px-5 py-4 font-mono text-xs text-slate-600">{b.booking_ref}</td>
                      <td className="px-5 py-4 font-medium text-slate-900">
                        {serviceBySlug(b.service)?.title || b.service}
                      </td>
                      <td className="px-5 py-4 text-slate-600">{fmtDate(b.preferred_date || b.created_at)}</td>
                      <td className="px-5 py-4"><StatusBadge value={b.status} /></td>
                      <td className="px-5 py-4">
                        <StatusBadge
                          value={b.payment_status === "unpaid" && b.payment_method === "offline" ? "pay_at_desk" : b.payment_status}
                        />
                        {canPay(b) && (
                          <button
                            onClick={() => payNow(b)}
                            data-testid={`booking-pay-now-${b.id}`}
                            className="mt-1.5 block text-xs font-semibold text-white bg-blue-900 rounded px-2.5 py-1 transition-colors duration-200 hover:bg-blue-800"
                          >
                            Pay Now
                          </button>
                        )}
                        {b.payment_status === "paid" && (
                          <Link
                            to={`/receipt/${b.id}`}
                            data-testid={`booking-receipt-${b.id}`}
                            className="mt-1.5 block text-xs font-semibold text-blue-900 hover:underline"
                          >
                            View receipt
                          </Link>
                        )}
                      </td>
                      <td className="px-5 py-4 font-medium text-slate-900">
                        {b.fee_paise > 0 ? `₹${b.fee_paise / 100}` : "Free"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-4 md:hidden">
              {bookings.map((b) => (
                <Reveal key={b.id}>
                  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`booking-card-${b.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-xs text-slate-500">{b.booking_ref}</span>
                      <StatusBadge value={b.status} />
                    </div>
                    <h3 className="mt-2 font-display text-lg font-semibold tracking-tight text-slate-900">
                      {serviceBySlug(b.service)?.title || b.service}
                    </h3>
                    <dl className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Date</dt>
                        <dd className="text-slate-900">{fmtDate(b.preferred_date || b.created_at)}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-500">Payment</dt>
                        <dd>
                          <StatusBadge
                            value={b.payment_status === "unpaid" && b.payment_method === "offline" ? "pay_at_desk" : b.payment_status}
                          />
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-slate-500">Amount</dt>
                        <dd className="font-medium text-slate-900">
                          {b.fee_paise > 0 ? `₹${b.fee_paise / 100}` : "Free"}
                        </dd>
                      </div>
                    </dl>
                    {canPay(b) && (
                      <button
                        onClick={() => payNow(b)}
                        data-testid={`booking-pay-now-${b.id}`}
                        className="mt-3 inline-block rounded-md bg-blue-900 px-4 py-2 text-xs font-semibold text-white transition-colors duration-200 hover:bg-blue-800"
                      >
                        Pay Now
                      </button>
                    )}
                    {b.payment_status === "paid" && (
                      <Link
                        to={`/receipt/${b.id}`}
                        data-testid={`booking-receipt-${b.id}`}
                        className="mt-3 inline-block text-sm font-semibold text-blue-900 hover:underline"
                      >
                        View receipt
                      </Link>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
